import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, User, Building2 } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const { login, homeFor } = useAuth();
  const [role, setRole] = useState("candidate");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      const u = data.user;
      if (u.role !== "admin" && u.role !== role) {
        toast.info(u.role === "company" ? "Tento účet je firemný – otváram firemnú zónu." : "Tento účet je kandidátsky – otváram kandidátsku zónu.");
      }
      nav(homeFor(u, role));
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail);
      if (status === 403 && detail.includes("schválen")) {
        toast.info(detail);
        nav("/pending");
      } else {
        toast.error(detail || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="mx-auto w-full max-w-md flex-1 flex flex-col px-6 pt-16">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-blue-700 flex items-center justify-center"><Sparkles className="text-white" size={20} /></div>
          <span className="font-head text-xl font-bold tracking-tight">Profeeluj<span className="text-blue-700"> AI</span></span>
        </div>
        <h1 className="mt-12 font-head text-3xl font-bold tracking-tight">Vitajte späť</h1>
        <p className="mt-2 text-slate-600">Prihláste sa do svojho účtu.</p>

        <div className="mt-6">
          <span className="text-sm font-semibold text-slate-700">Prihlásiť sa ako</span>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <RoleToggle active={role === "candidate"} onClick={() => setRole("candidate")} icon={User} title="Kandidát" testId="login-role-candidate" />
            <RoleToggle active={role === "company"} onClick={() => setRole("company")} icon={Building2} title="Firma" testId="login-role-company" />
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label="Email" type="email" value={email} onChange={setEmail} testId="login-email" />
          <Field label="Heslo" type="password" value={password} onChange={setPassword} testId="login-password" />
          <button data-testid="login-submit-btn" disabled={busy}
            className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-4 font-semibold transition-colors active:scale-95 disabled:opacity-60">
            {busy ? "Prihlasujem…" : "Prihlásiť"}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-600">
          Nemáte účet? <Link to="/register" className="font-semibold text-blue-700">Vytvoriť profil</Link>
        </p>
      </div>
    </div>
  );
}

function RoleToggle({ active, onClick, icon: Icon, title, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 font-semibold transition-colors ${active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
      <Icon size={18} /> {title}
    </button>
  );
}

export function Field({ label, type = "text", value, onChange, testId, placeholder }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <input
        data-testid={testId} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} required
        className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-colors"
      />
    </label>
  );
}
